import { useCallback, useMemo } from 'react'
import { useLoggedInUser } from '../../../../app/hooks/useLoggedInUser'
import {
  useGetAllHsHeadingsQuery,
  useGetAllSubHsHeadingsQuery,
} from '../../../../app/redux-fetch/apiProducts'
import { IHsHeading, IHsSubheading } from '../../types'

export interface Option {
  value: number
  label: string
}

interface UseHsCodeReturn {
  hsHeadingItems: IHsHeading[]
  hsSubheadingItems: IHsSubheading[]
  isLoading: boolean
  isError: boolean
  getSubheadingItems: (headingId?: number) => IHsSubheading[]
  mapToDropDownItems: (items: IHsHeading[] | IHsSubheading[]) => Option[]
  formatClassificationCode: (classificationCode?: string) => string
}

export const useHsCode = (options?: { skip?: boolean }): UseHsCodeReturn => {
  const { tenantId } = useLoggedInUser()
  const skip = options?.skip ?? false

  const {
    data: hsHeadingItems = [],
    isLoading: isHeadingsLoading,
    isError: isHeadingsError,
  } = useGetAllHsHeadingsQuery({ tenantId }, { skip })

  const {
    data: hsSubheadingItems = [],
    isLoading: isSubheadingsLoading,
    isError: isSubheadingsError,
  } = useGetAllSubHsHeadingsQuery({ tenantId }, { skip })

  const getSubheadingItems = useCallback(
    (headingId?: number): IHsSubheading[] => {
      if (!headingId) return []
      return hsSubheadingItems.filter((item) => item.hsHeadingId === headingId)
    },
    [hsSubheadingItems]
  )

  const mapToDropDownItems = useCallback((items: IHsHeading[] | IHsSubheading[]): Option[] => {
    return items.map((item) => {
      if ('subheadingCode' in item) {
        const code = item.subheadingCode
        const modifiedCode = `${code.slice(0, -2)} ${code.slice(-2)} - ${item.description}`
        return {
          value: item.id ?? 0,
          label: modifiedCode,
        }
      } else {
        return {
          value: item.id ?? 0,
          label: `${item.headingCode} - ${item.description}`,
        }
      }
    })
  }, [])

  const formatClassificationCode = useCallback((classificationCode?: string): string => {
    if (!classificationCode) return ''

    let formattedCode = classificationCode
    if (formattedCode.length > 4) {
      formattedCode = formattedCode.substring(0, 4) + ' ' + formattedCode.substring(4)
    }
    if (formattedCode.length > 7) {
      formattedCode = formattedCode.substring(0, 7) + ' ' + formattedCode.substring(7)
    }

    return formattedCode
  }, [])

  return useMemo(
    () => ({
      hsHeadingItems,
      hsSubheadingItems,
      isLoading: isHeadingsLoading || isSubheadingsLoading,
      isError: isHeadingsError || isSubheadingsError,
      getSubheadingItems,
      mapToDropDownItems,
      formatClassificationCode,
    }),
    [
      hsHeadingItems,
      hsSubheadingItems,
      isHeadingsLoading,
      isSubheadingsLoading,
      isHeadingsError,
      isSubheadingsError,
      getSubheadingItems,
      mapToDropDownItems,
      formatClassificationCode,
    ]
  )
}
